import React, { useState, useEffect, useRef } from 'react';
import { supabaseTracked } from '../lib/api/supabaseWithTracking';
import { ICONS_MAP, FOCUSED_INPUT_STYLE } from '../utils/constants';
import { useLanguage } from '../contexts/LanguageContext';

const DISMISSED_KEY = 'dismissed_website_orders';

const getDismissedIds = () => {
    try { return JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]'); }
    catch { return []; }
};
const addDismissedId = (id) => {
    const ids = getDismissedIds();
    if (!ids.includes(id)) {
        ids.push(id);
        localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids));
    }
};

export default function PendingWooOrdersModal({
    isOpen,
    onClose,
    processingOrders = [],
    instapayOrders = [],
    products,
    customers,
    bottles,
    oils,
    shippingFees,
    generalSettings,
    settings,
    formatCurrency,
    calculateBuildableStock,
    getProductCost,
    onSaveCustomer,
    onSaveOrder,
    showAlert,
    setView,
    setWooImportData,
    onOrderImported,          // (id) => removes from local state
    onStatusUpdate            // (id, newStatus) => updates DB
}) {
    const { t } = useLanguage();
    const [activeTab, setActiveTab] = useState('processing');
    const [mappingModalOpen, setMappingModalOpen] = useState(false);
    const [itemToMap, setItemToMap] = useState(null);
    const [savedMappings, setSavedMappings] = useState({});
    const [mapSearch, setMapSearch] = useState('');
    const [mapDropOpen, setMapDropOpen] = useState(false);
    const mapInputRef = useRef(null);
    const mapDropRef = useRef(null);

    // Auto-switch to instapay tab if no processing orders but instapay exist
    useEffect(() => {
        if (processingOrders.length === 0 && instapayOrders.length > 0 && activeTab === 'processing') {
            setActiveTab('instapay');
        } else if (instapayOrders.length === 0 && activeTab === 'instapay') {
            setActiveTab('processing');
        }
    }, [processingOrders.length, instapayOrders.length]);

    useEffect(() => {
        if (!isOpen) return;
        const fetchMappings = async () => {
            const { data, error } = await supabaseTracked.from('woo_product_map').select('*');
            if (data && !error) {
                const map = {};
                data.forEach(m => { map[m.woo_product_name] = m.crm_product_name || m.crm_product_id; });
                setSavedMappings(map);
            }
        };
        fetchMappings();
    }, [isOpen]);

    useEffect(() => {
        const handleClick = (e) => {
            if (mapDropRef.current && !mapDropRef.current.contains(e.target) &&
                mapInputRef.current && !mapInputRef.current.contains(e.target)) {
                setMapDropOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    if (!isOpen) return null;

    const currentOrders = activeTab === 'processing' ? processingOrders : instapayOrders;

    const normalize = s => (s || '').toLowerCase().replace(/[,\-\.]/g, ' ').replace(/\s+/g, ' ').trim();

    const getBestMatch = (itemName) => {
        if (!itemName) return null;
        if (savedMappings[itemName]) return savedMappings[itemName];
        const normName = normalize(itemName);
        const exact = products.find(p => normalize(p.product) === normName);
        if (exact) return exact.product;
        const words = normName.split(' ').filter(w => w.length > 2);
        const allWordMatch = products.find(p => {
            const pNorm = normalize(p.product);
            return words.every(w => pNorm.includes(w));
        });
        if (allWordMatch) return allWordMatch.product;
        const sizeMatch = itemName.match(/(\d+\s*ml)/i);
        const size = sizeMatch ? sizeMatch[1].replace(/\s+/g, '').toLowerCase() : null;
        const baseNameMatch = itemName.match(/^([^-,]+)/);
        if (baseNameMatch) {
            const baseName = baseNameMatch[1].trim().toLowerCase();
            const possibleMatches = products.filter(p => {
                const pLow = p.product.toLowerCase();
                return pLow.includes(baseName) && (!size || pLow.replace(/\s+/g, '').includes(size));
            });
            if (possibleMatches.length === 1) return possibleMatches[0].product;
        }
        return null;
    };

    const openMappingModal = (itemName, order) => {
        setItemToMap({ wooName: itemName, order });
        const best = getBestMatch(itemName);
        setMapSearch(best && best !== '__IGNORE__' ? best : '');
        setMapDropOpen(false);
        setMappingModalOpen(true);
    };

    const filteredCrmProducts = products
        .filter(p => normalize(p.product).includes(normalize(mapSearch)))
        .sort((a, b) => a.product.localeCompare(b.product));

    const findExistingCustomer = (order) => {
        if (!order) return null;
        const rawPhone = (order.phone || '').replace(/\D/g, '');
        const phoneBase = rawPhone.replace(/^0+|^20+/, '');
        let existing = customers.find(c => {
            if (phoneBase && c.mobile) {
                const cMobile = c.mobile.replace(/\D/g, '').replace(/^0+|^20+/, '');
                return cMobile === phoneBase;
            }
            return false;
        });
        if (!existing && order.email) {
            existing = customers.find(c => c.email?.toLowerCase() === order.email.toLowerCase());
        }
        return existing;
    };

    const Icon = ({ path, className = 'w-6 h-6' }) => (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
            <path d={path} />
        </svg>
    );

    const Button = ({ children, onClick, color = 'indigo', disabled = false, className = '' }) => (
        <button onClick={onClick} disabled={disabled} className={`px-4 py-2 bg-${color}-600 text-white rounded-md hover:bg-${color}-700 disabled:opacity-50 transition-colors ${className}`}>
            {children}
        </button>
    );

    const handleSaveMapping = async (wooName, crmName) => {
        if (!crmName) return;
        try {
            const { error } = await supabaseTracked.from('woo_product_map').upsert(
                { woo_product_name: wooName, crm_product_id: crmName },
                { onConflict: 'woo_product_name' }
            );
            if (error) throw error;
            setSavedMappings(prev => ({ ...prev, [wooName]: crmName }));
            setMappingModalOpen(false);
            setItemToMap(null);
            showAlert(`Mapped "${wooName}" → "${crmName}"`);
        } catch (err) {
            showAlert('Failed to save mapping');
        }
    };

    const constructSearchName = (name, sizeStr, attributeValue) => {
        let str = attributeValue || sizeStr || '';
        let parsedSize = '';
        let parsedGender = '';
        const sizeMatch = str.match(/(\d+\s*ml)/i);
        if(sizeMatch) parsedSize = sizeMatch[1].replace(/\s+/g,'').toLowerCase();
        if(/for\s*her/i.test(str)) parsedGender = 'For Her';
        else if(/for\s*him/i.test(str)) parsedGender = 'For Him';
        else if(/unisex/i.test(str)) parsedGender = 'Unisex';
        if(/tester/i.test(str) || /tester/i.test(name)) {
            return `${name.replace(/tester/i, '').trim()} - Tester - ${parsedSize || '5ml'}`.replace(/\s+-/g, ' -').replace(/-\s+/g, '- ');
        }
        if (parsedGender && parsedSize) return `${name} - ${parsedGender} - ${parsedSize}`;
        else if (parsedSize) return `${name} - ${parsedSize}`;
        else if (parsedGender) return `${name} - ${parsedGender}`;
        return name;
    };

    const getLineItems = (order) => {
        let extractedItems = [];
        (order.items || []).forEach(item => {
            if (item.bundleSelections && typeof item.bundleSelections === 'object' && Object.keys(item.bundleSelections).length > 0) {
                Object.values(item.bundleSelections).forEach(bundleItem => {
                    if (!bundleItem) return;
                    const name = bundleItem.name || '';
                    const sz = bundleItem.selectedVariant?.size || '';
                    const constructedName = constructSearchName(name, sz, bundleItem.selectedVariant?.attributeValue || '');
                    extractedItems.push({
                        name: constructedName,
                        originalName: name,
                        quantity: (bundleItem.quantity || 1) * (item.quantity || 1),
                        price: 0,
                        size: sz
                    });
                });
            } else {
                const constructedName = constructSearchName(item.name || '', item.size || '', '');
                extractedItems.push({
                    name: constructedName,
                    originalName: item.name || '',
                    quantity: item.quantity || 1,
                    price: item.price || 0,
                    size: item.size || ''
                });
            }
        });
        return extractedItems;
    };

    const mapPaymentMethod = (paymentMethod) => {
        if (!paymentMethod) return 'Visa';
        return paymentMethod.toLowerCase().includes('cash on delivery') ? 'Cash' : 'Visa';
    };

    const handleStartImport = (order) => {
        const existingCustomer = findExistingCustomer(order);
        const lineItems = getLineItems(order);
        const missingMappings = [];
        lineItems.forEach(item => {
            const match = getBestMatch(item.name);
            if (!match) missingMappings.push(item.name);
        });
        if (missingMappings.length > 0) {
            showAlert(`Please map missing products first: ${missingMappings[0]}`);
            openMappingModal(missingMappings[0], order);
            return;
        }
        const prefilledItems = lineItems
            .filter(item => {
                const m = getBestMatch(item.name);
                return m && m !== '__IGNORE__';
            })
            .map(item => ({ pid: getBestMatch(item.name), qty: item.quantity, used: false }));
        const mappedPayment = mapPaymentMethod(order.payment_method);
        const importPayload = {
            customerId: existingCustomer ? existingCustomer.id : '',
            items: prefilledItems,
            total: order.total,
            payment: mappedPayment,
            notes: order.notes || '',
            attachment: '',
            rawCustomerData: {
                first_name: (order.customer || '').split(' ')[0] || '',
                last_name: (order.customer || '').split(' ').slice(1).join(' ') || '',
                phone: order.phone || '',
                email: order.email || '',
                city: order.city || '',
                address: order.address || '',
                customer_note: order.notes || '',
            },
            woo_order_id: order.id,
            wooOrderData: order,
        };
        if (typeof onOrderImported === 'function') onOrderImported(order.id);
        setWooImportData(importPayload);
        onClose();
        if (!existingCustomer) { setView('New Customer'); showAlert('Customer not found.'); }
        else { setView('New Order'); showAlert('Details loaded.'); }
    };

    const handleDismiss = async (order) => {
        addDismissedId(order.id);
        try {
            const { lilacvalleyClient } = await import('../lib/api/lilacvalleyClient');
            await lilacvalleyClient.from('orders').update({ crm: 'dismissed' }).eq('id', order.id);
        } catch (e) { console.error(e); }
        if (typeof onOrderImported === 'function') onOrderImported(order.id);
        showAlert(`Dismissed Order #${order.id}`);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center p-4 border-b bg-indigo-600 text-white rounded-t-lg">
                    <h2 className="text-xl font-bold flex items-center gap-2">
                        <Icon path={ICONS_MAP['ordered']} />
                        🌐 Website Orders Management
                    </h2>
                    <button onClick={onClose} className="hover:text-gray-200">
                        <Icon path={ICONS_MAP['close']} />
                    </button>
                </div>

                {/* Tabs */}
                {instapayOrders.length > 0 && (
                    <div className="flex bg-gray-100 p-1 border-b">
                        <button 
                            onClick={() => setActiveTab('processing')}
                            className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${activeTab === 'processing' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            📦 Processing ({processingOrders.length})
                        </button>
                        <button 
                            onClick={() => setActiveTab('instapay')}
                            className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${activeTab === 'instapay' ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            💸 Instapay Pending ({instapayOrders.length})
                        </button>
                    </div>
                )}

                <div className="p-4 flex-1 overflow-y-auto bg-gray-50">
                    {currentOrders.length === 0 ? (
                        <div className="text-center p-8 text-gray-500">No {activeTab} orders found.</div>
                    ) : (
                        <div className="space-y-4">
                            {currentOrders.map(order => {
                                const lineItems = getLineItems(order);
                                const existingCustomer = findExistingCustomer(order);
                                return (
                                    <div key={order.id} className="bg-white p-4 rounded-lg shadow border border-gray-100">
                                        <div className="flex justify-between items-start mb-4">
                                            <div>
                                                <h3 className="font-bold text-lg text-indigo-900">Order #{order.id}</h3>
                                                <p className="text-sm text-gray-500">{new Date(order.created_at).toLocaleString()}</p>
                                                <p className="text-xs text-gray-400 mt-0.5">📍 {order.city} • {order.address}</p>
                                            </div>
                                            <div className="text-right flex flex-col items-end">
                                                <p className="font-bold text-lg">{order.total}</p>
                                                <p className="text-sm font-medium text-gray-800">{order.customer}</p>
                                                <p className="text-xs text-gray-500">{order.phone}</p>
                                                <div className="mt-1 flex gap-1">
                                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${order.payment_method?.toLowerCase().includes('cash') ? 'text-green-700 bg-green-100' : 'text-blue-700 bg-blue-100'}`}>
                                                        {order.payment_method}
                                                    </span>
                                                    {existingCustomer && <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">✓ CRM Customer</span>}
                                                </div>
                                            </div>
                                        </div>

                                        {order.notes && <div className="mb-3 bg-yellow-50 border border-yellow-200 rounded px-3 py-2 text-sm text-yellow-800">📝 {order.notes}</div>}

                                        {activeTab === 'instapay' && (
                                            <div className="mb-4">
                                                <h4 className="font-semibold text-gray-700 mb-2">Payment Proof:</h4>
                                                <div className="relative group cursor-pointer overflow-hidden rounded-lg border border-gray-200 bg-gray-100 aspect-video flex items-center justify-center">
                                                    <img 
                                                        src={`https://wrjmrnfvvqgthflwyfqo.supabase.co/storage/v1/object/public/Lilacvalley/payment-screenshots/${order.id}-payment-proof.jpg`} 
                                                        alt="Proof"
                                                        className="max-h-full object-contain"
                                                        onError={(e) => { if (!e.target.src.endsWith('.png')) e.target.src = e.target.src.replace('.jpg', '.png'); else e.target.style.display = 'none'; }}
                                                        onClick={(e) => window.open(e.target.src, '_blank')}
                                                    />
                                                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all">
                                                        <span className="bg-white px-3 py-1 rounded-full text-xs font-bold">Enlarge</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div className="mb-4 bg-gray-50 p-3 rounded text-sm">
                                            <h4 className="font-semibold text-gray-700 mb-2">Items:</h4>
                                            <ul className="space-y-1">
                                                {lineItems.map((item, idx) => (
                                                    <li key={idx} className="flex justify-between items-center border-b pb-1 last:border-b-0">
                                                        <span className="text-gray-800">{item.quantity}x {item.name}</span>
                                                        <span className="text-xs">
                                                            {getBestMatch(item.name) === '__IGNORE__' ? <span className="text-gray-500 bg-gray-100 px-2 py-1 rounded italic">Ignored</span> : 
                                                            getBestMatch(item.name) ? <button onClick={() => openMappingModal(item.name, order)} className="text-green-700 bg-green-100 px-2 py-1 rounded font-semibold">{getBestMatch(item.name)}</button> : 
                                                            <button onClick={() => openMappingModal(item.name, order)} className="text-red-500 border border-red-200 px-2 py-1 rounded bg-red-50 font-semibold">Unmapped</button>}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className="flex justify-end gap-2 border-t pt-3">
                                            <Button onClick={() => handleDismiss(order)} color="gray">Dismiss</Button>
                                            {activeTab === 'processing' ? (
                                                <Button onClick={() => handleStartImport(order)} color="green">Import to CRM</Button>
                                            ) : (
                                                <Button onClick={() => onStatusUpdate(order.id, 'processing')} color="purple">Verify & Move to Processing</Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* MAPPING MODAL OMISSIONS FOR BREVITY - ASSUMED INCOMPLETE BUT SHOULD BE RESTORED */}
            {mappingModalOpen && itemToMap && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-[60] p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <h3 className="text-lg font-bold mb-3 text-indigo-900">Map Website Product</h3>
                        <p className="text-sm text-gray-600 mb-4">Select CRM product for <br/><strong className="text-black bg-yellow-100 px-1">{itemToMap.wooName}</strong></p>
                        <div className="relative mb-4">
                            <input
                                ref={mapInputRef} type="text" value={mapSearch}
                                onChange={e => { setMapSearch(e.target.value); setMapDropOpen(true); }}
                                onFocus={() => setMapDropOpen(true)}
                                placeholder="Type to search..."
                                className="w-full p-2 border rounded-md outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            {mapDropOpen && filteredCrmProducts.length > 0 && (
                                <div ref={mapDropRef} className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg z-10 max-h-56 overflow-y-auto">
                                    {filteredCrmProducts.map(p => (
                                        <button key={p.product} className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 border-b last:border-0" onClick={() => { setMapSearch(p.product); setMapDropOpen(false); }}>
                                            {p.product}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col gap-3">
                            <div className="flex justify-end gap-2">
                                <Button onClick={() => { setMappingModalOpen(false); setItemToMap(null); }} color="gray">Cancel</Button>
                                <Button onClick={() => handleSaveMapping(itemToMap.wooName, mapSearch)} color="indigo" disabled={!mapSearch}>Save Mapping</Button>
                            </div>
                            <div className="border-t pt-3 flex justify-between items-center">
                                <span className="text-xs text-gray-500 w-2/3">Ignore non-product items.</span>
                                <Button onClick={() => handleSaveMapping(itemToMap.wooName, '__IGNORE__')} color="red">Ignore</Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
